import { GAME_CONFIG } from "../config/gameConfig";
import { RELIC_DUEL_PREVIEW_ENABLED } from "../config/buildFeatures";
import {
  ARENA_ART,
  ARENA_ASSET_COUNT,
  TROPHY_HALL_ART,
  WORKSHOP_ENERGY_ART,
} from "../config/artAssets";
import { amount } from "../domain/gameMath";
import type { DevScenarioId, GameState, ProgressSegment, SectionId } from "../domain/types";
import type { Translator } from "../i18n/locales";
import {
  formatAmount,
  formatCurrencyAmount,
  formatCurrencyUnit,
  formatPassiveRate,
  formatSeasonTime,
  formatUsdtCents,
  formatUsdtMicros,
  formatUtcDate,
} from "./formatters";
import { closeIcon, currencyIcon, sectionIcon } from "./icons";

export type SheetId = "balance" | "upgrade" | "match-exit" | null;
export type ArenaView = "catalog" | "penalty" | "relic-duel";

type TemplateContext = {
  state: GameState;
  serverNow: string;
  t: Translator;
};

export function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}

function currency(value: string, context: TemplateContext): string {
  const locale = context.state.settings.locale;
  return `<span class="currency-value">${currencyIcon()}<span>${formatAmount(value, locale)}</span><span class="visually-hidden"> ${escapeHtml(
    formatCurrencyUnit(value, locale),
  )}</span></span>`;
}

export function navigationTemplate(active: SectionId, t: Translator): string {
  const sections: readonly SectionId[] = ["core", "development", "progress", "friends", "games"];
  return sections
    .map(
      (section) => `<button
        class="nav-item${section === active ? " is-active" : ""}"
        type="button"
        data-action="navigate"
        data-section="${section}"
        aria-label="${escapeHtml(t(`nav.${section}`))}"
        ${section === active ? 'aria-current="page"' : ""}
      >
        <span class="nav-icon">${sectionIcon(section)}</span>
        <span class="nav-text">${escapeHtml(t(`nav.${section}`))}</span>
      </button>`,
    )
    .join("");
}

export function coreContextTemplate(context: TemplateContext): string {
  const { state, t } = context;
  if (state.season.status === "scheduled") {
    return `<span class="context-status">${escapeHtml(t("core.seasonScheduled"))}</span>`;
  }
  if (state.season.status === "frozen") {
    return `<button class="context-action" type="button" data-action="open-season">${escapeHtml(t("core.seasonFrozen"))}</button>`;
  }
  if (state.season.status === "claimable") {
    return `<button class="context-action is-ready" type="button" data-action="open-season">${escapeHtml(t("core.rewardReady"))}</button>`;
  }
  if (state.season.status === "closed") {
    return `<button class="context-action" type="button" data-action="open-season">${escapeHtml(t("core.rewardReceived"))}</button>`;
  }
  if (state.upgrades.mechanicalCutterLevel >= 1) {
    return `<button class="context-action is-running" type="button" data-action="navigate" data-section="development">
      ${escapeHtml(t("core.cutterWorking", { value: formatPassiveRate(state.account.passivePerHour, state.settings.locale) }))}
    </button>`;
  }

  const cost = GAME_CONFIG.economy.mechanicalCutterCost;
  const missing = amount(cost) - amount(state.account.available);
  const label =
    missing > 0n
      ? t("core.needMore", { value: formatCurrencyAmount(missing.toString(), state.settings.locale) })
      : t("core.nextUpgrade", { cost: formatCurrencyAmount(cost, state.settings.locale) });
  return `<button class="context-action${missing <= 0n ? " is-ready" : ""}" type="button" data-action="navigate" data-section="development">
    <span class="context-node" aria-hidden="true"></span>${escapeHtml(label)}
  </button>`;
}

export function developmentTemplate(context: TemplateContext): string {
  const { state, t } = context;
  const upgraded = state.upgrades.mechanicalCutterLevel >= 1;
  const cost = GAME_CONFIG.economy.mechanicalCutterCost;
  const missing = amount(cost) - amount(state.account.available);
  const canBuy = !upgraded && missing <= 0n && state.season.status === "active";
  const passive = formatPassiveRate(GAME_CONFIG.economy.mechanicalCutterPassivePerHour, state.settings.locale);
  const tap = formatAmount(state.account.tapReward, state.settings.locale);

  return `<div class="section-page development-page" data-testid="development-screen">
    <div class="section-heading">
      <div>
        <h1>${escapeHtml(t("development.title"))}</h1>
        <p>${escapeHtml(t("development.stage", { stage: state.workshopStage }))}</p>
      </div>
      <span class="stage-mark" aria-label="${escapeHtml(t("development.stage", { stage: state.workshopStage }))}">${state.workshopStage}</span>
    </div>

    <div class="mini-workshop" data-stage="${state.workshopStage}" aria-hidden="true">
      <div class="mini-ring"></div>
      <div class="mini-crystal"></div>
      <div class="mini-manual"></div>
      <div class="mini-mechanical"></div>
      <div class="mini-base"></div>
    </div>
    <p class="stage-caption">${escapeHtml(t("development.nextStage"))}</p>

    <section class="upgrade-branch" aria-labelledby="faceting-title">
      <div class="branch-heading"><span class="branch-line"></span><h2 id="faceting-title">${escapeHtml(t("development.faceting"))}</h2></div>
      <div class="upgrade-row is-static">
        <span class="upgrade-sigil sigil-facet" aria-hidden="true"></span>
        <div class="upgrade-copy">
          <strong>${escapeHtml(t("development.manualTool"))}</strong>
          <span>${escapeHtml(t("development.manualLevel"))}</span>
          <p>${escapeHtml(t("development.manualCurrent", { value: tap }))}</p>
          <small>${escapeHtml(t("development.manualNext"))}</small>
        </div>
      </div>
    </section>

    <section class="upgrade-branch" aria-labelledby="mechanisms-title">
      <div class="branch-heading"><span class="branch-line"></span><h2 id="mechanisms-title">${escapeHtml(t("development.mechanisms"))}</h2></div>
      <div class="upgrade-row${upgraded ? " is-installed" : ""}" data-testid="cutter-upgrade">
        <span class="upgrade-sigil sigil-mechanism" aria-hidden="true"></span>
        <div class="upgrade-copy">
          <strong>${escapeHtml(t("development.cutter"))}</strong>
          <span>${escapeHtml(t("development.level", { level: state.upgrades.mechanicalCutterLevel }))}</span>
          <p>${escapeHtml(
            upgraded
              ? t("development.currentActive", { value: passive })
              : t("development.currentIdle"),
          )}</p>
          <small>${escapeHtml(t("development.nextActive", { value: passive }))}</small>
          <button class="text-action" type="button" data-action="open-upgrade-details">${escapeHtml(t("development.details"))}</button>
        </div>
        <div class="upgrade-decision">
          <span class="price">${currency(cost, context)}</span>
          <button
            class="primary-button"
            type="button"
            data-action="buy-upgrade"
            data-testid="buy-cutter"
            ${upgraded || !canBuy ? "disabled" : ""}
          >${escapeHtml(upgraded ? t("development.installed") : t("development.upgrade", { cost: formatAmount(cost, state.settings.locale) }))}</button>
          ${
            !upgraded && missing > 0n
              ? `<span class="shortfall">${escapeHtml(t("development.notEnough", { value: formatCurrencyAmount(missing.toString(), state.settings.locale) }))}</span>
                 <button class="text-action" type="button" data-action="navigate" data-section="core">${escapeHtml(t("development.returnCore"))}</button>`
              : ""
          }
        </div>
      </div>
    </section>
  </div>`;
}

function progressTask(label: string, current: number, target: number, testId: string, context: TemplateContext): string {
  const complete = current >= target;
  const bounded = Math.min(current, target);
  return `<li class="task-row${complete ? " is-complete" : ""}" data-testid="${testId}">
    <span class="task-gem" aria-hidden="true"></span>
    <div class="task-copy"><strong>${escapeHtml(label)}</strong><span>${escapeHtml(
      complete ? context.t("progress.completed") : context.t("progress.taskValue", { current: bounded, target }),
    )}</span></div>
    <div class="task-meter" role="progressbar" aria-valuemin="0" aria-valuemax="${target}" aria-valuenow="${bounded}"><span style="--progress:${(bounded / target) * 100}%"></span></div>
  </li>`;
}

function todayTemplate(context: TemplateContext): string {
  const { state, t } = context;
  const targetFor = (metric: string): string =>
    GAME_CONFIG.liveOps.daily.tasks.find((task) => task.metric === metric)?.target ?? "0";
  const passiveTaskTarget = targetFor("passive-produced");
  const primaryTaskTarget = targetFor("primary-produced");
  const tapTaskTarget = Number(targetFor("confirmed-taps"));
  const passiveCurrent = Number(
    amount(state.progress.daily.passiveProduced) > amount(passiveTaskTarget)
      ? passiveTaskTarget
      : state.progress.daily.passiveProduced,
  );
  const passiveTarget = Number(passiveTaskTarget);
  const primaryCurrent = Number(
    amount(state.progress.daily.primaryProduced) > amount(primaryTaskTarget)
      ? primaryTaskTarget
      : state.progress.daily.primaryProduced,
  );
  const primaryTarget = Number(primaryTaskTarget);
  return `<ul class="task-list">
    ${progressTask(t("progress.taskTaps", { target: tapTaskTarget }), state.progress.daily.confirmedTaps, tapTaskTarget, "task-taps", context)}
    ${progressTask(t("hall.taskPrimaryBody", { target: primaryTarget }), primaryCurrent, primaryTarget, "task-primary", context)}
    ${progressTask(t("progress.taskPassive", { target: passiveTarget }), passiveCurrent, passiveTarget, "task-passive", context)}
  </ul><p class="section-note">${escapeHtml(t("progress.daySummary"))}</p>`;
}

function achievementsTemplate(context: TemplateContext): string {
  const { state, t } = context;
  const ready = state.upgrades.mechanicalCutterLevel >= 1;
  const claimed = state.progress.claimedAchievementIds.includes("living-facet");
  return `<div class="achievement-medallion${ready ? " is-ready" : ""}" aria-hidden="true"><span>Ⅰ</span></div>
    <div class="achievement-copy">
      <h2>${escapeHtml(t("progress.achievementTitle"))}</h2>
      <p>${escapeHtml(t("progress.achievementBody"))}</p>
      <span>${escapeHtml(
        claimed
          ? t("progress.achievementClaimed")
          : ready
            ? t("progress.achievementReady")
            : t("development.stage", { stage: 1 }),
      )}</span>
      <button class="primary-button" type="button" data-action="claim-achievement" ${!ready || claimed ? "disabled" : ""}>
        ${escapeHtml(claimed ? t("progress.achievementClaimed") : t("progress.achievementClaim"))}
      </button>
    </div>`;
}

function seasonTemplate(context: TemplateContext): string {
  const { state, serverNow, t } = context;
  const locale = state.settings.locale;
  const projectionVisible = state.season.projection.visible;
  const projectionRevealTime = state.season.projection.revealsAt === null
    ? t("common.soon")
    : formatSeasonTime(state.season.projection.revealsAt, serverNow, locale);
  const pendingClaim = state.seasonClaims.find((claim) => claim.status === "claimable");
  let status = `<div class="season-state"><h2>${escapeHtml(t("progress.activeSeasonTitle"))}</h2><p>${escapeHtml(
    t("progress.activeSeasonBody", { time: formatSeasonTime(state.season.endsAt, serverNow, locale) }),
  )}</p></div>`;

  if (pendingClaim) {
    status = `<div class="season-state is-reward"><h2>${escapeHtml(t("progress.rewardReadyTitle"))}</h2><p>${escapeHtml(
      t("progress.rewardReadyBody", { value: formatUsdtMicros(pendingClaim.referenceUsdtMicros, locale) }),
    )}</p><button class="primary-button" type="button" data-action="claim-season-reward" data-allocation="${escapeHtml(
      pendingClaim.allocationId,
    )}">${escapeHtml(t("progress.rewardClaimStars", { stars: pendingClaim.claimPriceStars }))}</button></div>`;
  } else if (state.season.status === "scheduled") {
    status = `<div class="season-state"><h2>${escapeHtml(t("progress.seasonScheduledTitle"))}</h2><p>${escapeHtml(
      t("progress.seasonScheduledBody", { date: formatUtcDate(state.season.startsAt, locale) }),
    )}</p></div>`;
  } else if (state.season.status === "frozen") {
    status = `<div class="season-state"><h2>${escapeHtml(t("progress.seasonFrozenTitle"))}</h2><p>${escapeHtml(t("progress.seasonFrozenBody"))}</p></div>`;
  } else if (state.season.status === "claimable") {
    status = `<div class="season-state is-reward"><h2>${escapeHtml(t("progress.rewardReadyTitle"))}</h2><p>${escapeHtml(
      t("progress.rewardReadyBody", { value: formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true) }),
    )}</p><button class="primary-button" type="button" data-action="claim-season-reward">${escapeHtml(t("progress.rewardClaim"))}</button></div>`;
  } else if (state.season.status === "closed") {
    status = `<div class="season-state is-claimed"><h2>${escapeHtml(t("progress.rewardClaimedTitle"))}</h2><p>${escapeHtml(t("progress.rewardClaimedBody"))}</p></div>`;
  }

  return `${status}
    <div class="season-pool"><span>${escapeHtml(t("progress.pool"))}</span><strong>${formatUsdtCents(state.season.prizeFundUsdCents, locale)}</strong></div>
    ${projectionVisible ? `<p class="estimate">${escapeHtml(t("progress.estimate", { value: formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true) }))}</p>
    <p class="section-note">${escapeHtml(t("progress.estimateNote"))}</p>
    <div class="ranking" aria-label="${escapeHtml(t("progress.ranking"))}">
      <h2>${escapeHtml(t("progress.ranking"))}</h2>
      <button type="button" class="primary-button" data-action="open-leaderboard">${escapeHtml(t("progress.ranking"))}</button>
    </div>` : `<div class="season-state"><h2>${escapeHtml(t("hall.projectionPreparingKicker"))}</h2><p>${escapeHtml(
      t("hall.projectionPreparing", { time: projectionRevealTime }),
    )}</p><p>${escapeHtml(t("hall.projectionPreparingBody"))}</p></div>`}
    <p class="snapshot-rule">${escapeHtml(t("progress.snapshotRule"))}</p>`;
}

export function progressTemplate(context: TemplateContext, activeSegment: ProgressSegment): string {
  const { t } = context;
  const segments: readonly ProgressSegment[] = ["today", "achievements", "season"];
  const content =
    activeSegment === "today"
      ? todayTemplate(context)
      : activeSegment === "achievements"
        ? achievementsTemplate(context)
        : seasonTemplate(context);

  return `<div class="section-page progress-page" data-testid="progress-screen">
    <div class="section-heading"><h1>${escapeHtml(t("progress.title"))}</h1></div>
    <div class="segment-control" role="tablist" aria-label="${escapeHtml(t("progress.title"))}">
      ${segments
        .map(
          (segment) => `<button type="button" role="tab" data-action="progress-segment" data-segment="${segment}" aria-selected="${segment === activeSegment}">${escapeHtml(t(`progress.${segment}`))}</button>`,
        )
        .join("")}
    </div>
    <section class="segment-content" role="tabpanel">${content}</section>
  </div>`;
}

export function friendsTemplate(context: TemplateContext): string {
  const { state, t } = context;
  const total = state.friends.reduce((sum, friend) => sum + amount(friend.primaryProduced), 0n).toString();
  const list = state.friends
    .map(
      (friend) => `<li class="friend-row">
        <span class="friend-avatar" aria-hidden="true">${escapeHtml(friend.name.slice(0, 1))}</span>
        <div><strong>${escapeHtml(friend.name)}</strong><span>${escapeHtml(
          friend.line === 1 ? t("friends.lineOne") : t("friends.lineTwo"),
        )}</span></div>
        <span>${escapeHtml(t("friends.contribution", { value: formatAmount(friend.primaryProduced, state.settings.locale) }))}</span>
      </li>`,
    )
    .join("");

  return `<div class="section-page friends-page" data-testid="friends-screen">
    <div class="section-heading"><h1>${escapeHtml(t("friends.title"))}</h1></div>
    <div class="constellation" aria-hidden="true"><span></span><span></span><span></span><i></i><i></i></div>
    <div class="friends-intro"><h2>${escapeHtml(t("friends.lead"))}</h2><p>${escapeHtml(t("friends.body"))}</p></div>
    <button class="primary-button invite-button" type="button" data-action="invite-friend">${escapeHtml(t("friends.invite"))}</button>
    ${
      state.friends.length === 0
        ? `<div class="empty-state"><span class="empty-orbit" aria-hidden="true"></span><h2>${escapeHtml(t("friends.emptyTitle"))}</h2><p>${escapeHtml(t("friends.emptyBody"))}</p></div>`
        : `<div class="friend-total"><span>${escapeHtml(t("friends.total", { value: formatAmount(total, state.settings.locale) }))}</span><strong>${state.friends.length}</strong></div><ul class="friend-list">${list}</ul>`
    }
  </div>`;
}

function arenaZoneTemplate(context: TemplateContext): string {
  const { state, t } = context;
  const selectedZone = state.pvp.currentTurn?.playerZone ?? null;
  return `<div class="arena-zones" data-testid="penalty-goal" aria-label="${escapeHtml(
    state.pvp.currentTurn?.role === "keeper" ? t("games.savePrompt") : t("games.zonePrompt"),
  )}">
    ${Array.from({ length: GAME_CONFIG.pvp.zoneCount }, (_, zone) => {
      const zoneKey = `games.zone.${zone + 1}` as Parameters<Translator>[0];
      const zoneName = t(zoneKey);
      const actionLabel = state.pvp.currentTurn?.role === "keeper"
        ? t("games.saveZone", { zone: zoneName })
        : t("games.shotZone", { zone: zoneName });
      return `<button class="arena-zone${selectedZone === zone ? " is-selected" : ""}" type="button" data-action="pvp-zone" data-zone="${zone}" aria-label="${escapeHtml(
        actionLabel,
      )}" ${state.pvp.status === "choosing" ? "" : "disabled"}><span aria-hidden="true"><i></i><b>${escapeHtml(zoneName)}</b></span></button>`;
    }).join("")}
  </div>`;
}

function arenaAttemptMarkers(
  match: GameState["pvp"],
  side: "player" | "opponent",
  t: Translator,
): string {
  const attempts = match.history.filter((turn) => (side === "player" ? turn.role === "shooter" : turn.role === "keeper"));
  const slotCount = Math.max(GAME_CONFIG.pvp.regulationSeries, attempts.length);
  const label = side === "player" ? t("games.attemptsYou") : t("games.attemptsOpponent");
  return `<span class="arena-attempts" data-arena-attempts="${side}" role="img" aria-label="${escapeHtml(label)}">${Array.from(
    { length: slotCount },
    (_, index) => {
      const attempt = attempts[index];
      if (attempt === undefined) return `<i class="arena-attempt is-pending" aria-hidden="true"></i>`;
      const scored = attempt.outcome === "goal";
      return `<i class="arena-attempt ${scored ? "is-goal" : "is-save"}" title="${escapeHtml(
        scored ? t("games.attemptGoal") : t("games.attemptSave"),
      )}"></i>`;
    },
  ).join("")}</span>`;
}

function arenaCallout(
  turn: GameState["pvp"]["history"][number] | undefined,
  t: Translator,
): { headline: string; detail: string; success: boolean } {
  if (turn?.role === "keeper") {
    if (turn.outcome === "save") return { headline: t("games.save"), detail: t("games.calloutYouSaved"), success: true };
    return { headline: t("games.goal"), detail: t("games.calloutYouConceded"), success: false };
  }
  if (turn?.outcome === "save") return { headline: t("games.save"), detail: t("games.calloutOpponentSaved"), success: false };
  return { headline: t("games.goal"), detail: t("games.calloutYouScored"), success: true };
}

function arenaResultCopy(state: GameState, t: Translator): { title: string; body: string } {
  if (state.pvp.result === "win") return { title: t("games.resultWin"), body: t("games.resultWinBody") };
  if (state.pvp.result === "loss") return { title: t("games.resultLoss"), body: t("games.resultLossBody") };
  if (state.pvp.result === "draw") return { title: t("games.resultDraw"), body: t("games.resultDrawBody") };
  return { title: t("games.resultCancelled"), body: t("games.resultCancelledBody") };
}

function arenaResultPayout(state: GameState, t: Translator): string {
  const value = formatCurrencyAmount(state.pvp.payout, state.settings.locale);
  return state.pvp.result === "draw" || state.pvp.result === "cancelled"
    ? t("games.resultRefund", { value })
    : t("games.resultPayout", { value });
}

function arenaCatalogTemplate(context: TemplateContext): string {
  const { state, t } = context;
  const artById = {
    "crystal-penalty": ARENA_ART.ball,
    "resonance-rush": WORKSHOP_ENERGY_ART.sigil,
    "relic-duel": TROPHY_HALL_ART.seasonCrown,
  } as const;
  const copyById = {
    "crystal-penalty": {
      title: t("games.penalty"),
      body: t("games.penaltyBody"),
      tag: t("games.catalogPenaltyTag"),
    },
    "resonance-rush": {
      title: t("games.catalogResonance"),
      body: t("games.catalogResonanceBody"),
      tag: t("games.catalogSoon"),
    },
    "relic-duel": {
      title: t("games.catalogRelic"),
      body: t("games.catalogRelicBody"),
      tag: RELIC_DUEL_PREVIEW_ENABLED ? t("games.catalogRelicTag") : t("games.catalogSoon"),
    },
  } as const;
  const cards = GAME_CONFIG.arenaCatalog.map((entry, index) => {
    const art = artById[entry.id];
    const copy = copyById[entry.id];
    const playable = entry.availability === "playable" || (entry.id === "relic-duel" && RELIC_DUEL_PREVIEW_ENABLED);
    return `<button class="arena-game-card${playable ? " is-playable" : " is-locked"}" type="button" data-action="${
      playable ? "open-arena-game" : "arena-coming-soon"
    }" data-game="${entry.id}" aria-label="${escapeHtml(`${copy.title}. ${playable ? t("games.catalogOpen") : t("games.catalogSoon")}`)}">
      <span class="arena-game-card__number" aria-hidden="true">0${index + 1}</span>
      <span class="arena-game-card__art" aria-hidden="true"><i></i><img src="${escapeHtml(art.url)}" alt=""></span>
      <span class="arena-game-card__copy"><small>${escapeHtml(copy.tag)}</small><strong>${escapeHtml(copy.title)}</strong><span>${escapeHtml(
        copy.body,
      )}</span></span>
      <span class="arena-game-card__status">${escapeHtml(playable ? t("games.catalogEnter") : t("games.catalogSoon"))}<i aria-hidden="true">${
        playable ? "→" : "◇"
      }</i></span>
    </button>`;
  }).join("");
  return `<div class="arena-catalog-page" data-testid="games-screen">
    <div class="arena-catalog-runtime" id="arena-catalog-runtime" aria-hidden="true"></div>
    <div class="arena-catalog-shade" aria-hidden="true"></div>
    <section class="arena-catalog-intro" aria-labelledby="arena-catalog-title">
      <span>${escapeHtml(t("games.catalogKicker"))}</span>
      <h2 id="arena-catalog-title">${escapeHtml(t("games.catalogTitle"))}</h2>
      <p>${escapeHtml(t("games.catalogBody"))}</p>
    </section>
    <section class="arena-game-grid" aria-label="${escapeHtml(t("games.catalogTitle"))}">${cards}</section>
    <footer class="arena-catalog-balance"><span>${escapeHtml(t("games.available"))}</span>${currency(
      state.account.available,
      context,
    )}</footer>
  </div>`;
}

function relicDuelTemplate(_context: TemplateContext): string {
  return `<div class="relic-duel-page" data-testid="relic-duel-screen">
    <div class="relic-duel-runtime" id="relic-duel-runtime"></div>
    <p class="visually-hidden" aria-live="polite" data-relic-duel-announcement></p>
  </div>`;
}

export function gamesTemplate(
  context: TemplateContext,
  view: ArenaView = "catalog",
  manualGuardianSupported = false,
): string {
  const { state, t } = context;
  const locale = state.settings.locale;
  const match = state.pvp;
  const canStart = amount(state.account.available) >= amount(GAME_CONFIG.pvp.stake) && state.season.status === "active";
  const missing = amount(GAME_CONFIG.pvp.stake) - amount(state.account.available);
  const result = arenaResultCopy(state, t);
  const lastTurn = match.history.at(-1);
  const currentSeries = match.currentTurn?.series ?? lastTurn?.series ?? 1;
  const seriesText =
    currentSeries <= GAME_CONFIG.pvp.regulationSeries
      ? t("games.series", { current: currentSeries, total: GAME_CONFIG.pvp.regulationSeries })
      : t("games.suddenDeath", { current: currentSeries - GAME_CONFIG.pvp.regulationSeries });
  const roleText = match.currentTurn?.role === "keeper" ? t("games.roleSave") : t("games.roleShot");
  const promptText = match.currentTurn?.role === "keeper" ? t("games.savePrompt") : t("games.zonePrompt");
  const callout = arenaCallout(lastTurn, t);
  const opponentName = match.opponentName ?? t("games.scoreOpponent");
  const scoreOpponentName = match.opponentKind === "guardian" ? t("games.scoreOpponent") : opponentName;
  const arenaFallbackArt = match.currentTurn?.role === "keeper" ? ARENA_ART.defenseBackground : ARENA_ART.background;

  if (view === "relic-duel" && RELIC_DUEL_PREVIEW_ENABLED) return relicDuelTemplate(context);
  if (view === "catalog" && match.status === "idle") return arenaCatalogTemplate(context);

  return `<div class="arena-page" data-testid="games-screen" data-arena-status="${match.status}" data-arena-result="${match.result ?? "none"}" data-arena-role="${match.currentTurn?.role ?? "none"}">
    <img class="arena-runtime-fallback" src="${escapeHtml(arenaFallbackArt.url)}" alt="" width="${arenaFallbackArt.width}" height="${arenaFallbackArt.height}" decoding="async" data-visual-asset="arena-background" aria-hidden="true">
    <div class="arena-runtime-shell" aria-hidden="true">
      <div class="arena-runtime" id="arena-runtime"></div>
      <div class="arena-runtime-loader">
        <i class="arena-runtime-loader__mark"></i>
        <span data-arena-loader-phase>${escapeHtml(t("games.loadingArena"))}</span>
        <strong data-arena-loader-percent>0%</strong>
        <div class="arena-runtime-loader__track"><i></i></div>
        <small data-arena-loader-count>${escapeHtml(t("games.loadingCount", { loaded: 0, total: ARENA_ASSET_COUNT }))}</small>
      </div>
    </div>
    <div class="arena-veil" aria-hidden="true"></div>

    <div class="arena-balance" aria-label="${escapeHtml(t("games.available"))}">
      <span class="arena-balance__sigil" aria-hidden="true">${currencyIcon()}</span>
      <span><small>${escapeHtml(t("games.available"))}</small><strong data-arena-balance>${escapeHtml(
        formatAmount(state.account.available, locale, true),
      )}</strong></span>
    </div>

    <div class="arena-score" data-arena-score>
      <div class="arena-score__names"><small>${escapeHtml(t("games.scoreYou"))}</small><small data-arena-opponent-name>${escapeHtml(scoreOpponentName)}</small></div>
      <div class="arena-score__numbers"><strong data-arena-score-player>${match.score.player}</strong><i aria-hidden="true">—</i><strong data-arena-score-opponent>${match.score.opponent}</strong></div>
      <div class="arena-score__attempt-row">${arenaAttemptMarkers(match, "player", t)}${arenaAttemptMarkers(match, "opponent", t)}</div>
    </div>

    <div class="arena-series" data-arena-series>${escapeHtml(seriesText)}</div>

    <section class="arena-panel arena-panel--lobby" data-arena-panel="lobby">
      <button class="arena-catalog-back" type="button" data-action="arena-catalog"><span aria-hidden="true">←</span>${escapeHtml(
        t("games.catalogBack"),
      )}</button>
      <span class="arena-kicker">${escapeHtml(t("games.title"))}</span>
      <h2>${escapeHtml(t("games.penalty"))}</h2>
      <p>${escapeHtml(t("games.penaltyBody"))}</p>
      <button class="arena-rules-button" type="button" data-action="open-arena-rules"><i aria-hidden="true">?</i><span>${escapeHtml(
        t("games.rules"),
      )}</span></button>
      <div class="arena-pot">
        <span><small>${escapeHtml(t("games.stake"))}</small><strong>${currency(GAME_CONFIG.pvp.stake, context)}</strong></span>
        <i aria-hidden="true">×2</i>
        <span><small>${escapeHtml(t("games.bank"))}</small><strong>${currency((amount(GAME_CONFIG.pvp.stake) * 2n).toString(), context)}</strong></span>
      </div>
      <p class="arena-stake-note">${escapeHtml(
        t("games.eachStake", { value: formatCurrencyAmount(GAME_CONFIG.pvp.stake, locale) }),
      )}</p>
      <button class="arena-primary" type="button" data-action="start-match" ${canStart ? "" : "disabled"}>${escapeHtml(
        t("games.playFor", { value: formatAmount(GAME_CONFIG.pvp.stake, locale) }),
      )}</button>
      <small class="arena-shortfall" data-arena-shortfall ${missing > 0n ? "" : "hidden"}>${escapeHtml(
        t("games.notEnough", { value: formatCurrencyAmount((missing > 0n ? missing : 0n).toString(), locale) }),
      )}</small>
    </section>

    <section class="arena-panel arena-panel--search" data-arena-panel="matchmaking">
      <span class="arena-kicker">${escapeHtml(t("games.searching"))}</span>
      <h2>${escapeHtml(t("games.searchTitle"))}</h2>
      <p data-arena-search-copy>${escapeHtml(t("games.searchBody", { seconds: Math.ceil(GAME_CONFIG.pvp.matchmakingMs / 1_000) }))}</p>
      ${manualGuardianSupported ? `<button class="arena-guardian-choice" type="button" data-action="request-guardian" hidden><span aria-hidden="true">◆</span><strong>${escapeHtml(
        t("games.guardianChoice"),
      )}</strong><small>${escapeHtml(t("games.guardianChoiceBody"))}</small></button>` : ""}
      <button class="arena-secondary" type="button" data-action="cancel-match">${escapeHtml(t("games.cancel"))}</button>
    </section>

    <section class="arena-panel arena-panel--found" data-arena-panel="intro">
      <span class="arena-kicker">${escapeHtml(t("games.found"))}</span>
      <h2 data-arena-intro-opponent>${escapeHtml(opponentName)}</h2>
      <p>${escapeHtml(t("games.guardianReady"))}</p>
    </section>

    <section class="arena-turn" data-arena-panel="turn">
      <div class="arena-turn__copy">
        <span data-arena-role>${escapeHtml(roleText)}</span>
        <strong data-arena-prompt>${escapeHtml(match.status === "move-accepted" ? t("games.moveAccepted") : promptText)}</strong>
        <small data-arena-wait ${match.status === "move-accepted" ? "" : "hidden"}>${escapeHtml(t("games.waitOpponent"))}</small>
      </div>
      <div class="arena-timer" role="timer" aria-label="${escapeHtml(t("games.turnTime"))}">
        <svg viewBox="0 0 44 44" aria-hidden="true"><circle cx="22" cy="22" r="18"></circle><circle cx="22" cy="22" r="18" data-arena-timer-ring></circle></svg>
        <strong data-arena-timer>${Math.round(GAME_CONFIG.pvp.turnMs / 1_000)}</strong>
      </div>
    </section>

    ${arenaZoneTemplate(context)}

    <section class="arena-reveal" data-arena-panel="resolved" data-arena-player-outcome="${callout.success ? "success" : "failure"}" aria-live="polite">
      <span data-arena-callout>${escapeHtml(callout.headline)}</span>
      <strong data-arena-callout-detail>${escapeHtml(callout.detail)}</strong>
      <small data-arena-auto ${lastTurn?.playerAuto === true ? "" : "hidden"}>${escapeHtml(t("games.autoMove"))}</small>
    </section>

    <section class="arena-panel arena-panel--result" data-arena-panel="result" data-testid="pvp-result">
      <span class="arena-kicker" data-arena-result-series>${escapeHtml(seriesText)}</span>
      <h2 data-arena-result-title>${escapeHtml(result.title)}</h2>
      <p data-arena-result-body>${escapeHtml(result.body)}</p>
      <strong class="arena-result-bank" data-arena-result-bank>${escapeHtml(arenaResultPayout(state, t))}</strong>
      <div class="arena-result-actions">
        <button class="arena-primary" type="button" data-action="start-match" ${canStart ? "" : "disabled"}>${escapeHtml(t("games.playAgain"))}</button>
        <button class="arena-secondary" type="button" data-action="return-arena">${escapeHtml(t("games.returnArena"))}</button>
      </div>
    </section>
  </div>`;
}

export function profileTemplate(context: TemplateContext, devMode: boolean): string {
  const { state, t } = context;
  const settings: readonly [keyof Pick<GameState["settings"], "music" | "effects" | "vibration" | "reducedMotion">, string][] = [
    ["music", "profile.music"],
    ["effects", "profile.effects"],
    ["vibration", "profile.vibration"],
    ["reducedMotion", "profile.reducedMotion"],
  ];
  return `<button class="modal-backdrop" type="button" data-action="close-profile" aria-label="${escapeHtml(t("profile.close"))}"></button>
    <section class="profile-panel" role="dialog" aria-modal="true" aria-labelledby="profile-title">
      <button class="icon-button close-button" type="button" data-action="close-profile" aria-label="${escapeHtml(t("profile.close"))}">${closeIcon()}</button>
      <div class="profile-identity"><span class="large-avatar" aria-hidden="true">A</span><div><h2 id="profile-title">${escapeHtml(t("profile.name"))}</h2><p>${escapeHtml(t("profile.subtitle"))}</p></div></div>
      <div class="settings-list">
        ${settings
          .map(
            ([key, label]) => `<label class="setting-row"><span>${escapeHtml(t(label as Parameters<Translator>[0]))}</span><input type="checkbox" name="${key}" data-setting="${key}" ${state.settings[key] ? "checked" : ""}/><i aria-hidden="true"></i></label>`,
          )
          .join("")}
      </div>
      <div class="language-row"><span>${escapeHtml(t("profile.language"))}</span><button type="button" data-action="set-locale" data-locale="ru" aria-pressed="${state.settings.locale === "ru"}">${escapeHtml(t("profile.russian"))}</button><button type="button" data-action="set-locale" data-locale="en" aria-pressed="${state.settings.locale === "en"}">${escapeHtml(t("profile.english"))}</button></div>
      <p class="settings-hint">${escapeHtml(t("profile.settingsHint"))}</p>
      ${devMode ? `<button class="danger-text" type="button" data-action="reset-demo">${escapeHtml(t("profile.reset"))}</button>` : ""}
    </section>`;
}

export function sheetTemplate(sheet: SheetId, context: TemplateContext): string {
  if (sheet === null) return "";
  const { state, t } = context;
  let body: string;
  if (sheet === "balance") {
    body = `<h2 id="sheet-title">${escapeHtml(t("balance.title"))}</h2>
      <dl class="balance-details"><div><dt>${escapeHtml(t("balance.available"))}</dt><dd>${currency(state.account.available, context)}</dd></div><div><dt>${escapeHtml(t("balance.reserved"))}</dt><dd>${currency(state.account.reserved, context)}</dd></div><div><dt>${escapeHtml(t("balance.production"))}</dt><dd>${escapeHtml(t("balance.unitPerSecond", { value: formatPassiveRate(state.account.passivePerHour, state.settings.locale) }))}</dd></div></dl>`;
  } else if (sheet === "upgrade") {
    body = `<h2 id="sheet-title">${escapeHtml(t("development.sheetTitle"))}</h2><p>${escapeHtml(t("development.sheetBody"))}</p>
      <dl class="upgrade-details"><div><dt>${escapeHtml(t("development.sheetCurrent"))}</dt><dd>${escapeHtml(t("development.currentIdle"))}</dd></div><div><dt>${escapeHtml(t("development.sheetAfter"))}</dt><dd>${escapeHtml(t("development.nextActive", { value: formatPassiveRate(GAME_CONFIG.economy.mechanicalCutterPassivePerHour, state.settings.locale) }))}</dd></div></dl><p class="visual-change-note">${escapeHtml(t("development.sheetVisual"))}</p>`;
  } else {
    body = `<h2 id="sheet-title">${escapeHtml(t("games.leaveTitle"))}</h2><p>${escapeHtml(t("games.leaveBody"))}</p><div class="sheet-actions"><button class="primary-button" type="button" data-action="confirm-leave-match">${escapeHtml(t("games.leave"))}</button><button class="secondary-button" type="button" data-action="close-sheet">${escapeHtml(t("games.stay"))}</button></div>`;
  }
  return `<button class="modal-backdrop" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}"></button><section class="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-title"><button class="icon-button close-button" type="button" data-action="close-sheet" aria-label="${escapeHtml(t("sheet.close"))}">${closeIcon()}</button>${body}</section>`;
}

const devScenarios: readonly [DevScenarioId, Parameters<Translator>[0]][] = [
  ["first-entry", "dev.firstEntry"],
  ["after-upgrade", "dev.afterUpgrade"],
  ["insufficient-funds", "dev.insufficient"],
  ["network-loss", "dev.network"],
  ["partial-taps", "dev.partial"],
  ["offline-production", "dev.offline"],
  ["projection-warmup", "dev.projectionWarmup"],
  ["season-scheduled", "dev.scheduled"],
  ["season-scheduled-with-claim", "dev.scheduledWithClaim"],
  ["season-frozen", "dev.frozen"],
  ["season-settling", "dev.settling"],
  ["reward-ready", "dev.rewardReady"],
  ["reward-received", "dev.rewardReceived"],
  ["ton-usdt-prize-active", "dev.tonUsdtPrizeActive"],
  ["ton-usdt-prize-winner", "dev.tonUsdtPrizeWinner"],
  ["community-missing", "dev.communityMissing"],
  ["empty-friends", "dev.emptyFriends"],
  ["arena-ready", "dev.arenaReady"],
  ["arena-result-stack", "dev.arenaResultStack"],
  ["active-pvp", "dev.activePvp"],
  ["large-balance", "dev.largeBalance"],
  ["visual-stage-3", "dev.visualStage3"],
  ["visual-stage-4", "dev.visualStage4"],
  ["energy-empty", "dev.energyEmpty"],
  ["reduced-motion", "dev.reduced"],
];

export function devPanelTemplate(t: Translator, collapsed: boolean): string {
  if (collapsed) {
    return `<button class="dev-toggle" type="button" data-action="toggle-dev">DEV</button>`;
  }
  return `<aside class="dev-panel" aria-label="${escapeHtml(t("dev.title"))}"><div class="dev-heading"><strong>DEV</strong><button type="button" data-action="toggle-dev" aria-label="${escapeHtml(t("dev.hide"))}">×</button></div><div class="dev-scenarios">${devScenarios
    .map(([id, label]) => `<button type="button" data-action="seed-scenario" data-scenario="${id}">${escapeHtml(t(label))}</button>`)
    .join("")}</div></aside>`;
}

export const renderNavigation = navigationTemplate;

export function renderDevelopment(state: GameState, t: Translator, _locale: GameState["settings"]["locale"]): string {
  return developmentTemplate({ state, serverNow: state.meta.lastProductionAt, t });
}

export function renderProgress(
  state: GameState,
  segment: ProgressSegment,
  serverNow: string,
  t: Translator,
  _locale: GameState["settings"]["locale"],
): string {
  return progressTemplate({ state, serverNow, t }, segment);
}

export function renderFriends(state: GameState, t: Translator, _locale: GameState["settings"]["locale"]): string {
  return friendsTemplate({ state, serverNow: state.meta.lastProductionAt, t });
}

export function renderGames(
  state: GameState,
  t: Translator,
  _locale: GameState["settings"]["locale"],
  view: ArenaView = "catalog",
  manualGuardianSupported = false,
): string {
  return gamesTemplate({ state, serverNow: state.meta.lastProductionAt, t }, view, manualGuardianSupported);
}

export function renderProfileSheet(state: GameState, t: Translator, devMode = false): string {
  return profileTemplate({ state, serverNow: state.meta.lastProductionAt, t }, devMode);
}

export function renderBalanceSheet(state: GameState, t: Translator, _locale: GameState["settings"]["locale"]): string {
  return sheetTemplate("balance", { state, serverNow: state.meta.lastProductionAt, t });
}

export function renderUpgradeSheet(state: GameState, t: Translator, _locale: GameState["settings"]["locale"]): string {
  return sheetTemplate("upgrade", { state, serverNow: state.meta.lastProductionAt, t });
}

export function renderDevPanel(t: Translator, collapsed = false): string {
  return devPanelTemplate(t, collapsed);
}
